/**
 * RBAC-PILOT-ORDERS-001 — Orders pilot for the Roles & Permission Matrix.
 *
 * Additive semantics: a user reaches Orders when their role already allows
 * it OR the matrix grants `orders:view`. For the Jurisdiction Requestor the
 * hard redirect is relaxed ONLY for `/dashboard/orders` and ONLY while an
 * `orders:view` grant exists; the Jurisdiction Dashboard stays the landing.
 */
export const JR_ORDERS_PATH = '/dashboard/orders';
const ALWAYS_ALLOWED = new Set(['/choose-workspace']);
// OD-PARITY-001 — Order Desk workspace paths and the permission each one needs.
// The route guard is shell-level only; every view/action re-checks permissions
// and the backend stays authoritative.
const WORKSPACE_PATHS = {
  [JR_ORDERS_PATH]: ['orders', 'view'],
  '/dashboard/favorites': ['orders', 'view'],
  '/dashboard/reports': ['reports', 'view'],
};
// Prefix routes (Order Details and its sub-tabs) — backend re-checks every read.
const WORKSPACE_PREFIXES = [['/orders/', ['orders', 'view']]];

const requiredFor = (pathname) => {
  if (WORKSPACE_PATHS[pathname]) return WORKSPACE_PATHS[pathname];
  const hit = WORKSPACE_PREFIXES.find(([prefix]) => pathname.startsWith(prefix));
  return hit ? hit[1] : null;
};

/**
 * @returns {'allow'|'pending'|'redirect'}
 *   allow    — JR holds the permission required for this Order Desk workspace path
 *   pending  — permissions still loading for a workspace path (do not redirect yet)
 *   redirect — every other operational /dashboard path
 */
export const jrOrdersAccess = (pathname, hasPermission, permissionsLoading) => {
  if (ALWAYS_ALLOWED.has(pathname)) return 'allow';
  const required = requiredFor(pathname);
  if (!required) return 'redirect';
  if (hasPermission(...required)) return 'allow';
  return permissionsLoading ? 'pending' : 'redirect';
};
